import React, { useState, useEffect } from 'react';
import { CrawlResponse } from '../types';
import { toast } from 'react-toastify';
import { colors, typography, spacing } from '../styles/design-system';
import { User } from '../api/auth';
import MatchListPanel from './MatchListPanel';
import RawDataPanel from './RawDataPanel';
import PredictionPanel from './PredictionPanel';

interface DataDisplayProps {
  data: CrawlResponse;
  onRefresh?: () => void;
  isLoading?: boolean;
  currentUser?: User | null;
}

const DataDisplay: React.FC<DataDisplayProps> = ({ data: initialData, onRefresh, isLoading = false, currentUser }) => {
  const [data, setData] = useState(initialData);
  const [activeTab, setActiveTab] = useState<'matches' | 'raw' | 'predictions'>('matches');
  const [selectedMatch, setSelectedMatch] = useState(0);
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showIframe, setShowIframe] = useState(false);
  const [showMatchSelector, setShowMatchSelector] = useState(false);
  const [selectorStep, setSelectorStep] = useState<'date' | 'sport' | 'match'>('date');
  const [tempSelectedDate, setTempSelectedDate] = useState<string>('');
  const [tempSelectedSport, setTempSelectedSport] = useState<string>('');

  const isHockey = (data?.data?.[selectedMatch]?.match || data?.data?.[selectedMatch])?.sport === 'hockey';

  // 필터링된 데이터 계산
  const filteredData = React.useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter((item) => {
      const match = item?.match || item;
      if (selectedSport && match?.sport !== selectedSport) return false;
      if (selectedDate && match?.match_date !== selectedDate) return false;
      return true;
    });
  }, [data, selectedSport, selectedDate]);

  // 필터링된 데이터에서 현재 선택된 경기
  const currentMatch = filteredData[selectedMatch];

  // Normalize match data structure
  const matchData = currentMatch?.match || currentMatch;

  // 모달에서 사용할 임시 필터링 데이터
  const tempFilteredData = React.useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter((item) => {
      const match = item?.match || item;
      if (tempSelectedSport && match?.sport !== tempSelectedSport) return false;
      if (tempSelectedDate && match?.match_date !== tempSelectedDate) return false;
      return true;
    });
  }, [data, tempSelectedSport, tempSelectedDate]);

  // 사용 가능한 날짜 목록
  const availableDates = React.useMemo(() => {
    if (!data?.data) return [];
    const dates = new Set<string>();
    data.data.forEach(item => {
      const match = item?.match || item;
      if (match?.match_date) {
        dates.add(match.match_date);
      }
    });
    return Array.from(dates).sort();
  }, [data]);

  // 선택된 날짜에서 사용 가능한 종목 목록 (순서: 축구, 야구, 농구, 배구, 하키)
  const availableSports = React.useMemo(() => {
    if (!data?.data) return [];
    const sportOrder = ['soccer', 'baseball', 'basketball', 'volleyball', 'hockey'];
    const sports = new Set<string>();
    data.data.forEach(item => {
      const match = item?.match || item;
      if (tempSelectedDate && match?.match_date !== tempSelectedDate) return;
      if (match?.sport) {
        sports.add(match.sport);
      }
    });
    return Array.from(sports).sort((a, b) => {
      const orderA = sportOrder.indexOf(a);
      const orderB = sportOrder.indexOf(b);
      if (orderA === -1 && orderB === -1) return a.localeCompare(b);
      if (orderA === -1) return 1;
      if (orderB === -1) return -1;
      return orderA - orderB;
    });
  }, [data, tempSelectedDate]);

  // 모달 열기
  const openMatchSelector = () => {
    setTempSelectedDate(selectedDate);
    setTempSelectedSport(selectedSport);
    setSelectorStep('date');
    setShowMatchSelector(true);
    document.body.style.overflow = 'hidden';
  };

  // 모달 닫기
  const closeMatchSelector = () => {
    setShowMatchSelector(false);
    setTempSelectedDate(selectedDate);
    setTempSelectedSport(selectedSport);
    document.body.style.overflow = '';
  };

  // 경기 선택 완료
  const handleMatchSelect = (index: number) => {
    setSelectedDate(tempSelectedDate);
    setSelectedSport(tempSelectedSport);
    setSelectedMatch(index);
    setActiveTab('matches');
    setShowMatchSelector(false);
    document.body.style.overflow = '';
  };

  // 종목 라벨 가져오기
  const getSportLabel = (sport: string) => {
    switch (sport) {
      case 'soccer': return '축구';
      case 'baseball': return '야구';
      case 'basketball': return '농구';
      case 'volleyball': return '배구';
      case 'hockey': return '하키';
      default: return '기타';
    }
  };

  // 현재 선택된 경기 요약 텍스트
  const getSelectedMatchSummary = () => {
    if (!currentMatch) return '경기를 선택해야 함';
    const match = currentMatch.match || currentMatch;
    const dateStr = match?.match_date ? match.match_date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3') : '';
    return `${dateStr} ${getSportLabel(match?.sport || '')} ${match?.home_team || ''} vs ${match?.away_team || ''}`;
  };

  // 컴포넌트 언마운트 시 스크롤 복원
  React.useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Update data when prop changes
  React.useEffect(() => {
    setData(initialData);
    setSelectedMatch(0);
  }, [initialData]);

  // 필터 변경 시 selectedMatch가 유효한 범위 내에 있도록 조정
  React.useEffect(() => {
    if (selectedMatch >= filteredData.length && filteredData.length > 0) {
      setSelectedMatch(0);
    }
  }, [selectedSport, selectedDate, filteredData.length, selectedMatch]);

  if (!data || data.data.length === 0) {
    return (
      <div style={styles.noData}>
        <div style={{marginBottom: '20px'}}>
          {isLoading ? '경기 정보를 불러오는 중...' : '조회된 경기가 없음.'}
        </div>
        {!isLoading && onRefresh && (
          <button
            onClick={onRefresh}
            style={{
              padding: '8px 20px',
              backgroundColor: '#000',
              color: '#fff',
              border: '1px solid #000',
              borderRadius: '0',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              outline: 'none'
            }}
          >
            새로고침
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Match Selector Modal - 3 Column Layout */}
      {showMatchSelector && (
        <div style={styles.modalOverlay} onClick={closeMatchSelector}>
          <div style={styles.columnModalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>경기 선택</h3>
              <button
                style={styles.modalCloseButton}
                onClick={closeMatchSelector}
              >
                ✕
              </button>
            </div>

            {/* 3 Column Layout */}
            <div style={styles.columnContainer}>
              {/* Column 1: Date */}
              <div style={styles.column}>
                <div style={styles.columnHeader}>날짜</div>
                <div style={styles.columnList}>
                  <div
                    style={{
                      ...styles.columnItem,
                      ...(tempSelectedDate === '' ? styles.columnItemSelected : {})
                    }}
                    onClick={() => {
                      setTempSelectedDate('');
                      setTempSelectedSport('');
                    }}
                  >
                    <span style={styles.columnItemText}>전체</span>
                    <span style={styles.columnItemCount}>{data.data.length}</span>
                  </div>
                  {availableDates.map(date => {
                    const count = data.data.filter(item => {
                      const match = item?.match || item;
                      return match?.match_date === date;
                    }).length;
                    return (
                      <div
                        key={date}
                        style={{
                          ...styles.columnItem,
                          ...(tempSelectedDate === date ? styles.columnItemSelected : {})
                        }}
                        onClick={() => {
                          setTempSelectedDate(date);
                          setTempSelectedSport('');
                        }}
                      >
                        <span style={styles.columnItemText}>{date.replace(/(\d{4})(\d{2})(\d{2})/, '$2/$3')}</span>
                        <span style={styles.columnItemCount}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 2: Sport */}
              <div style={styles.column}>
                <div style={styles.columnHeader}>종목</div>
                <div style={styles.columnList}>
                  <div
                    style={{
                      ...styles.columnItem,
                      ...(tempSelectedSport === '' ? styles.columnItemSelected : {})
                    }}
                    onClick={() => setTempSelectedSport('')}
                  >
                    <span style={styles.columnItemText}>전체</span>
                    <span style={styles.columnItemCount}>
                      {data.data.filter(item => {
                        const match = item?.match || item;
                        return !tempSelectedDate || match?.match_date === tempSelectedDate;
                      }).length}
                    </span>
                  </div>
                  {availableSports.map(sport => {
                    const count = data.data.filter(item => {
                      const match = item?.match || item;
                      return (!tempSelectedDate || match?.match_date === tempSelectedDate) && match?.sport === sport;
                    }).length;
                    return (
                      <div
                        key={sport}
                        style={{
                          ...styles.columnItem,
                          ...(tempSelectedSport === sport ? styles.columnItemSelected : {})
                        }}
                        onClick={() => setTempSelectedSport(sport)}
                      >
                        <span style={styles.columnItemText}>{getSportLabel(sport)}</span>
                        <span style={styles.columnItemCount}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 3: Match */}
              <div style={{...styles.column, flex: 2}}>
                <div style={styles.columnHeader}>경기 ({tempFilteredData.length})</div>
                <div style={styles.columnList}>
                  {tempFilteredData.length === 0 ? (
                    <div style={styles.noMatchMessage}>
                      해당 조건의 경기가 없음
                    </div>
                  ) : (
                    tempFilteredData.map((item, index) => {
                      const match = item?.match || item;
                      return (
                        <div
                          key={index}
                          style={styles.matchColumnItem}
                          onClick={() => handleMatchSelect(index)}
                        >
                          <div style={styles.matchColumnInfo}>
                            <div style={styles.matchColumnTeams}>
                              {match?.home_team || '?'} vs {match?.away_team || '?'}
                            </div>
                            <div style={styles.matchColumnMeta}>
                              {match?.match_time || ''} · {match?.league || ''}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.container}>
      {/* Loading Message */}
      {isLoading && (
        <div style={styles.loadingMessage}>
          경기 정보를 불러오는 중...
        </div>
      )}

      {/* Match Selector Button */}
      <div style={styles.filterContainer}>
        <div style={styles.singleFilterRow}>
          <button
            onClick={openMatchSelector}
            disabled={isLoading}
            style={styles.matchSelectorButton}
          >
            <span style={styles.matchSelectorText}>{getSelectedMatchSummary()}</span>
            <span style={styles.matchSelectorArrow}>▼</span>
          </button>

          {onRefresh && (
            <span
              onClick={isLoading ? undefined : onRefresh}
              style={{
                ...styles.refreshIcon,
                color: isLoading ? colors.gray[400] : colors.gray[600],
                cursor: isLoading ? 'not-allowed' : 'pointer',
                animation: isLoading ? 'spin 1s linear infinite' : 'none',
              }}
              title='경기 정보 새로고침'
            >
              ↻
            </span>
          )}
        </div>
      </div>

      {/* Common Match Info - Always visible */}
      {filteredData.length === 0 ? (
        <div style={styles.commonInfoSection}>
          <div style={{textAlign: 'center', padding: '40px', color: '#6c757d'}}>
            <div style={{fontSize: '18px', marginBottom: '10px'}}>
              선택한 조건에 해당하는 경기가 없음.
            </div>
            <div style={{fontSize: '14px'}}>
              다른 종목이나 날짜를 선택해.
            </div>
          </div>
        </div>
      ) : matchData && (
      <div style={styles.commonInfoSection}>
        <h3 style={styles.sectionTitle}>경기 정보</h3>
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>경기 일시:</span>
            <span style={styles.infoValue}>
              {matchData?.match_date || '날짜 미정'} {matchData?.match_time || ''}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>경기장:</span>
            <span style={styles.infoValue}>
              {matchData?.venue || '미정'}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>리그:</span>
            <span style={styles.infoValue}>
              {matchData?.league || ''}
            </span>
          </div>
          {matchData?.match_status && (
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>경기 상태:</span>
              <span style={{
                ...styles.infoValue,
                color: matchData.match_status === '경기전' ? colors.status.success :
                       matchData.match_status === '경기중' ? colors.status.warning : colors.status.error,
                fontWeight: typography.fontWeight.semibold
              }}>
                {matchData.match_status}
              </span>
            </div>
          )}
          {matchData?.skip_reason && (
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>참고:</span>
              <span style={{...styles.infoValue, color: colors.status.error}}>
                {matchData.skip_reason}
              </span>
            </div>
          )}
          {matchData?.game_id && (
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>경기 ID:</span>
              <button
                style={styles.linkButton}
                onClick={() => setShowIframe(!showIframe)}
              >
                {matchData.game_id} {showIframe ? '▼' : '▶'}
              </button>
            </div>
          )}
        </div>

        {/* Iframe container */}
        {showIframe && matchData?.game_id && (
          <div style={styles.iframeContainer}>
            <iframe
              src={matchData?.sport === 'hockey'
                ? (matchData.match_url
                    ? `https://www.livesport.com${matchData.match_url.startsWith('/') ? '' : '/'}${matchData.match_url}`
                    : `https://www.livesport.com/kr/match/${matchData.game_id}/`)
                : `https://spodb.spojoy.com/?game_id=${matchData.game_id}`}
              style={styles.iframe}
              title="Match Details"
            />
          </div>
        )}
      </div>
      )}

      {/* Tabs - 필터링된 데이터가 있을 때만 표시 */}
      {filteredData.length > 0 && (
      <div style={styles.tabs}>
        <button
          style={{...styles.tab, ...(activeTab === 'matches' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('matches')}
        >
          경기 데이터
        </button>
        <button
          style={{...styles.tab, ...(activeTab === 'raw' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('raw')}
        >
          원본 데이터
        </button>
        <button
          style={{...styles.tab, ...(activeTab === 'predictions' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('predictions')}
        >
          예측 리포트
        </button>
      </div>
      )}

      {/* Content Container - 필터링된 데이터가 있을 때만 표시 */}
      {filteredData.length > 0 && (
      <div style={styles.contentContainer}>
        {activeTab === 'matches' && (
          <div style={styles.tabContent}>
            <MatchListPanel
              matchData={matchData}
              currentMatch={currentMatch}
            />
          </div>
        )}

        {activeTab === 'raw' && (
          <div style={styles.tabContent}>
            <RawDataPanel currentMatch={currentMatch} />
          </div>
        )}

        {activeTab === 'predictions' && (
          <div style={styles.tabContent}>
            <PredictionPanel
              currentMatch={currentMatch}
              data={data}
              setData={setData}
              isLoading={isLoading}
              currentUser={currentUser}
              setActiveTab={setActiveTab}
            />
          </div>
        )}
      </div>
      )}
    </div>
    </>
  );
};

// Exported styles - shared with child components (MatchListPanel, RawDataPanel, PredictionPanel)
export const dataDisplayStyles = {
  container: {
    padding: spacing[3],
    backgroundColor: colors.gray[50],
    marginTop: '0',
    border: 'none',
    maxHeight: 'calc(100vh - 60px)',
    overflow: 'auto' as const,
  },
  loadingMessage: {
    padding: spacing[4],
    marginBottom: spacing[3],
    backgroundColor: colors.gray[100],
    color: colors.gray[700],
    border: `1px solid ${colors.gray[200]}`,
    textAlign: 'center' as const,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
  },
  matchSelector: {
    marginBottom: spacing[6],
    padding: spacing[5],
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[200]}`,
  },
  filterContainer: {
    marginBottom: spacing[3],
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[200]}`,
  },
  singleFilterRow: {
    display: 'flex',
    gap: spacing[3],
    alignItems: 'center',
  },
  refreshIcon: {
    fontSize: '20px',
    fontWeight: typography.fontWeight.bold,
    padding: spacing[2],
    transition: 'color 0.2s',
  },
  compactFilterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[2],
  },
  compactLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[600],
  },
  compactSelect: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.fontSize.base,
    color: colors.gray[800],
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[300]}`,
    cursor: 'pointer',
    outline: 'none',
    minWidth: '120px',
  },
  compactPsynetButton: {
    padding: `${spacing[2]} ${spacing[5]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
    alignSelf: 'flex-end',
  },
  commonInfoSection: {
    marginBottom: spacing[5],
    padding: spacing[5],
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[200]}`,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[5],
    color: colors.black,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 2fr))',
    gap: spacing[3],
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
    whiteSpace: 'nowrap' as const,
  },
  infoLabel: {
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[600],
    minWidth: '40px',
  },
  infoValue: {
    color: colors.gray[800],
  },
  linkButton: {
    padding: `${spacing[1]} ${spacing[3]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  iframeContainer: {
    marginTop: spacing[5],
    padding: spacing[4],
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
  },
  iframe: {
    width: '100%',
    height: '600px',
    border: 'none',
  },
  select: {
    marginLeft: spacing[3],
    padding: `${spacing[2]} ${spacing[4]}`,
    backgroundColor: colors.white,
    color: colors.gray[700],
    border: `1px solid ${colors.gray[300]}`,
    fontSize: typography.fontSize.base,
  },
  teamButtons: {
    width: '100%',
    display: 'flex',
    marginBottom: spacing[4],
  },
  teamButton: {
    width: '100%',
    flex: 1,
    padding: spacing[3],
    backgroundColor: colors.white,
    color: colors.gray[600],
    border: `1px solid ${colors.gray[300]}`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  selectedTeamButton: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },
  tabs: {
    width: '100%',
    display: 'flex',
    marginBottom: '0',
    borderBottom: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
    gap: 0,
  },
  tab: {
    width: '100%',
    padding: `${spacing[3]}`,
    backgroundColor: 'transparent',
    color: colors.gray[600],
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    transition: 'all 0.2s',
    marginBottom: '-1px',
    whiteSpace: 'nowrap' as const,
  },
  activeTab: {
    backgroundColor: 'transparent',
    color: colors.black,
    borderBottom: `2px solid ${colors.black}`,
    fontWeight: typography.fontWeight.semibold,
  },
  contentContainer: {
    backgroundColor: colors.white,
    padding: spacing[4],
    borderTop: 'none',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '60vh',
    overflow: 'auto' as const,
  },
  statsTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: spacing[3],
    border: `1px solid ${colors.gray[200]}`,
  },
  summaryTable: {
    width: 'auto',
    borderCollapse: 'collapse' as const,
    border: `1px solid ${colors.gray[200]}`,
  },
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: spacing[3],
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[200]}`,
  },
  statsHeader: {
    padding: spacing[3],
    backgroundColor: colors.gray[100],
    borderTop: 'none',
    borderLeft: 'none',
    borderBottom: `1px solid ${colors.gray[300]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[800],
  },
  statsCell: {
    padding: spacing[3],
    borderTop: 'none',
    borderLeft: 'none',
    borderBottom: `1px solid ${colors.gray[200]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[700],
  },
  tableHeader: {
    padding: spacing[3],
    backgroundColor: colors.gray[100],
    borderTop: 'none',
    borderLeft: 'none',
    borderBottom: `1px solid ${colors.gray[300]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[800],
  },
  tableCell: {
    padding: `${spacing[2]} ${spacing[3]}`,
    borderTop: 'none',
    borderLeft: 'none',
    borderBottom: `1px solid ${colors.gray[200]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    fontSize: typography.fontSize.base,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[700],
  },
  statsBlock: {
    marginBottom: spacing[6],
    padding: spacing[5],
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
  },
  recentForm: {
    marginTop: spacing[6],
    padding: spacing[5],
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
  },
  matchInfoSection: {
    marginBottom: spacing[6],
  },
  teamStatsSection: {
    marginTop: spacing[6],
    padding: spacing[5],
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
  },
  modelSelection: {
    marginBottom: spacing[6],
  },
  modelButtons: {
    display: 'flex',
    marginTop: spacing[3],
  },
  modelButton: {
    padding: `${spacing[3]} ${spacing[6]}`,
    backgroundColor: colors.white,
    color: colors.gray[600],
    border: `1px solid ${colors.gray[300]}`,
    cursor: 'pointer',
    fontWeight: typography.fontWeight.medium,
    transition: 'all 0.2s',
  },
  selectedModelButton: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },
  predictionControlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[4],
    marginBottom: spacing[5],
    paddingBottom: spacing[5],
    borderBottom: `1px solid ${colors.gray[200]}`,
    flexWrap: 'wrap' as const,
  },
  modelSelectGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
  },
  modelSelectLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[700],
    whiteSpace: 'nowrap' as const,
  },
  modelSelect: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
    cursor: 'pointer',
    minWidth: '120px',
  },
  reportSelectGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    flex: 1,
  },
  reportSelect: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
    cursor: 'pointer',
    minWidth: '200px',
    maxWidth: '350px',
  },
  promptButton: {
    padding: `${spacing[3]} ${spacing[6]}`,
    backgroundColor: colors.gray[600],
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  generateButton: {
    padding: `${spacing[3]} ${spacing[6]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  predictionContent: {
    flex: 1,
  },
  predictionMeta: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing[4],
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
    borderBottom: 'none',
    fontSize: typography.fontSize.sm,
    color: colors.gray[700],
  },
  predictionMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
  },
  prediction: {
    maxHeight: '50vh',
    overflow: 'auto' as const,
    backgroundColor: colors.gray[50],
    padding: spacing[5],
    marginTop: 0,
    border: `1px solid ${colors.gray[200]}`,
  },
  rawDataControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[4],
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[200]}`,
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    fontSize: typography.fontSize.base,
    color: colors.gray[700],
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  rawDataContainer: {
    position: 'relative' as const,
  },
  rawData: {
    backgroundColor: colors.gray[50],
    padding: spacing[5],
    border: `1px solid ${colors.gray[200]}`,
    maxHeight: '50vh',
    overflowY: 'auto' as const,
  },
  rawDataCopyButton: {
    padding: `${spacing[2]} ${spacing[5]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    transition: 'all 0.2s',
  },
  pre: {
    margin: 0,
    color: colors.gray[800],
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily,
    whiteSpace: 'pre-wrap' as const,
    lineHeight: typography.lineHeight.relaxed,
  },
  copyButton: {
    marginTop: spacing[3],
    padding: `${spacing[2]} ${spacing[5]}`,
    backgroundColor: colors.gray[600],
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
  },
  notionLink: {
    marginTop: spacing[6],
    textAlign: 'center' as const,
  },
  link: {
    color: colors.gray[700],
    textDecoration: 'underline',
    fontSize: typography.fontSize.base,
  },
  noData: {
    padding: spacing[10],
    textAlign: 'center' as const,
    color: colors.gray[500],
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: colors.white,
    maxWidth: '600px',
    maxHeight: '80vh',
    width: '90%',
    display: 'flex',
    flexDirection: 'column' as const,
    border: `1px solid ${colors.gray[200]}`,
  },
  modalHeader: {
    padding: spacing[6],
    borderBottom: `1px solid ${colors.gray[200]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: typography.fontSize['2xl'],
    cursor: 'pointer',
    color: colors.gray[500],
    padding: '0',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBody: {
    padding: spacing[6],
    flex: 1,
    overflow: 'auto',
  },
  modalPre: {
    margin: 0,
    whiteSpace: 'pre-wrap' as const,
    fontSize: typography.fontSize.base,
    lineHeight: typography.lineHeight.relaxed,
    color: colors.gray[800],
  },
  modalFooter: {
    padding: spacing[6],
    borderTop: `1px solid ${colors.gray[200]}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing[3],
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    margin: 0,
  },
  modalCopyButton: {
    padding: `${spacing[3]} ${spacing[6]}`,
    backgroundColor: colors.gray[600],
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  modalConfirmButton: {
    padding: `${spacing[3]} ${spacing[6]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  modalPrimaryButton: {
    padding: `${spacing[2]} ${spacing[6]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  modalSecondaryButton: {
    padding: `${spacing[2]} ${spacing[6]}`,
    backgroundColor: colors.white,
    color: colors.black,
    border: `1px solid ${colors.black}`,
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  modalCancelButton: {
    padding: `${spacing[2]} ${spacing[6]}`,
    backgroundColor: colors.white,
    color: colors.gray[600],
    border: `1px solid ${colors.gray[400]}`,
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  promptTextarea: {
    width: '100%',
    height: '400px',
    padding: spacing[4],
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamilyMono,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    resize: 'vertical' as const,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  promptHint: {
    marginTop: spacing[4],
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
  },
  detailButton: {
    padding: `${spacing[2]} ${spacing[4]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  gameIframeContainer: {
    marginTop: spacing[6],
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[200]}`,
    overflow: 'hidden',
  },
  iframeHeader: {
    padding: spacing[5],
    borderBottom: `1px solid ${colors.gray[200]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.gray[50],
  },
  gameIframe: {
    width: '100%',
    height: '600px',
    border: 'none',
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[3],
  },
  providerTabs: {
    display: 'flex',
    gap: spacing[2],
  },
  providerTab: {
    padding: `${spacing[2]} ${spacing[4]}`,
    backgroundColor: colors.gray[100],
    color: colors.gray[600],
    border: `1px solid ${colors.gray[200]}`,
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    transition: 'all 0.2s',
  },
  activeProviderTab: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },
  promptEditor: {
    marginTop: spacing[6],
    padding: spacing[5],
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
  },
  editorButtons: {
    display: 'flex',
    gap: spacing[3],
    marginTop: spacing[3],
    justifyContent: 'flex-end',
  },
  saveButton: {
    padding: `${spacing[2]} ${spacing[5]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  cancelButton: {
    padding: `${spacing[2]} ${spacing[5]}`,
    backgroundColor: colors.gray[600],
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  predictionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[3],
  },
  predictionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[4],
    marginBottom: spacing[4],
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[200]}`,
  },
  reportCopyButton: {
    padding: `${spacing[2]} ${spacing[4]}`,
    backgroundColor: colors.gray[600],
    color: colors.white,
    border: `1px solid ${colors.gray[600]}`,
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
    whiteSpace: 'nowrap' as const,
  },
  promptViewButton: {
    padding: `${spacing[2]} ${spacing[4]}`,
    backgroundColor: colors.white,
    color: colors.gray[700],
    border: `1px solid ${colors.gray[300]}`,
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
    whiteSpace: 'nowrap' as const,
  },
  psynetButton: {
    padding: `${spacing[2]} ${spacing[4]}`,
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    whiteSpace: 'nowrap' as const,
  },
  matchSelectorButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: spacing[4],
    padding: `${spacing[4]} ${spacing[5]}`,
    backgroundColor: colors.white,
    border: `1px solid ${colors.black}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left' as const,
  },
  matchSelectorIcon: {
    fontSize: typography.fontSize.xl,
  },
  matchSelectorText: {
    flex: 1,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[800],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  matchSelectorArrow: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${spacing[5]} ${spacing[6]}`,
    borderBottom: `1px solid ${colors.gray[200]}`,
    backgroundColor: colors.gray[50],
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: spacing[1],
    opacity: 0.5,
  },
  stepItemActive: {
    opacity: 1,
  },
  stepNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: colors.black,
    color: colors.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
  },
  stepLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    fontWeight: typography.fontWeight.medium,
  },
  stepConnector: {
    width: '40px',
    height: '2px',
    backgroundColor: colors.gray[200],
    margin: 0,
  },
  stepContent: {
    padding: `${spacing[3]} 0`,
  },
  stepTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[3],
    color: colors.gray[800],
  },
  stepSubtitle: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
    marginBottom: spacing[5],
  },
  optionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: spacing[3],
  },
  optionButton: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: spacing[2],
    padding: `${spacing[5]} ${spacing[4]}`,
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[300]}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  optionButtonSelected: {
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.black}`,
  },
  optionIcon: {
    fontSize: typography.fontSize['2xl'],
  },
  optionText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[800],
  },
  optionCount: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
  },
  matchList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[2],
    maxHeight: '300px',
    overflowY: 'auto' as const,
  },
  matchItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${spacing[4]} ${spacing[5]}`,
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[200]}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left' as const,
  },
  matchItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[4],
    flex: 1,
    overflow: 'hidden',
  },
  matchItemSport: {
    fontSize: typography.fontSize['2xl'],
  },
  matchItemInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  matchItemTeams: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[800],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  matchItemMeta: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    marginTop: spacing[1],
  },
  matchItemArrow: {
    fontSize: typography.fontSize.md,
    color: colors.gray[600],
    marginLeft: spacing[2],
  },
  noMatchMessage: {
    padding: spacing[8],
    textAlign: 'center' as const,
    color: colors.gray[500],
    fontSize: typography.fontSize.md,
  },
  backButton: {
    marginTop: spacing[5],
    padding: `${spacing[3]} ${spacing[5]}`,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.gray[500]}`,
    color: colors.gray[600],
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
    transition: 'all 0.2s',
  },
  columnModalContent: {
    backgroundColor: colors.white,
    width: '95%',
    maxWidth: '900px',
    height: '500px',
    display: 'flex',
    flexDirection: 'column' as const,
    border: `1px solid ${colors.gray[200]}`,
    overflow: 'hidden',
  },
  columnContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    borderTop: `1px solid ${colors.gray[200]}`,
    maxHeight: '60vh',
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    borderRight: `1px solid ${colors.gray[200]}`,
    minWidth: 0,
    height: '100%',
    overflow: 'hidden',
  },
  columnHeader: {
    padding: `${spacing[4]} ${spacing[5]}`,
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[200]}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[700],
    flexShrink: 0,
  },
  columnList: {
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    padding: spacing[2],
  },
  columnItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    padding: `${spacing[3]} ${spacing[4]}`,
    marginBottom: spacing[1],
    backgroundColor: colors.white,
    border: '1px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  columnItemSelected: {
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.black}`,
  },
  columnItemIcon: {
    fontSize: typography.fontSize.md,
    flexShrink: 0,
  },
  columnItemText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.gray[800],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  columnItemCount: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    backgroundColor: colors.gray[100],
    padding: `${spacing[1]} ${spacing[3]}`,
    flexShrink: 0,
  },
  matchColumnItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
    padding: `${spacing[3]} ${spacing[4]}`,
    marginBottom: spacing[1],
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[200]}`,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  matchColumnIcon: {
    fontSize: typography.fontSize.xl,
    flexShrink: 0,
  },
  matchColumnInfo: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  matchColumnTeams: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[800],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  matchColumnMeta: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    marginTop: spacing[1],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
};

// Internal alias for parent component usage
const styles = dataDisplayStyles;

export default DataDisplay;
